<template>
  <div class="tm-wrap">

    <!-- ── Header ── -->
    <div class="tm-header">
      <div class="tm-header-left">
        <div class="tm-header-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="#E86A10" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="7" y1="7" x2="7.01" y2="7" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <div>
          <div class="tm-header-title">Google Tag Manager</div>
          <div class="tm-header-sub">Configure o GTM para rastrear conversões, eventos e métricas do site</div>
        </div>
      </div>
    </div>

    <!-- ── Banner informativo ── -->
    <div class="tm-banner">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#E86A10" stroke-width="2"/>
        <path d="M12 16v-4M12 8h.01" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <span>Cole o <strong>Container ID</strong> do Google Tag Manager (ex: <code>GTM-XXXXXXX</code>). O código será injetado automaticamente em todas as páginas do site quando ativo.</span>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="tm-loading">
      <div class="tm-spinner"></div>
      <span>Carregando configuração...</span>
    </div>

    <!-- ── Erro ── -->
    <div v-else-if="erro" class="tm-error">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#f87171" stroke-width="2"/>
        <path d="M12 8v4M12 16h.01" stroke="#f87171" stroke-width="2" stroke-linecap="round"/>
      </svg>
      {{ erro }}
    </div>

    <!-- ── Formulário ── -->
    <div v-else class="tm-card">

      <!-- Status toggle -->
      <div class="tm-status-bar" :class="{ 'tm-status-bar--on': form.ativo }">
        <div class="tm-status-info">
          <div class="tm-status-dot" :class="{ 'tm-status-dot--on': form.ativo }"></div>
          <div>
            <div class="tm-status-label">{{ form.ativo ? 'Tag Manager ativo' : 'Tag Manager inativo' }}</div>
            <div class="tm-status-desc">{{ form.ativo ? 'O GTM está sendo injetado em todas as páginas' : 'Ative para começar a rastrear eventos' }}</div>
          </div>
        </div>
        <label class="tm-toggle">
          <input type="checkbox" v-model="form.ativo" />
          <span class="tm-toggle-track">
            <span class="tm-toggle-thumb"></span>
          </span>
        </label>
      </div>

      <!-- GTM ID -->
      <div class="tm-field">
        <label class="tm-label">Container ID <span class="tm-required">*</span></label>
        <div class="tm-input-wrap">
          <span class="tm-input-prefix">GTM-</span>
          <input
            v-model="gtmSuffix"
            class="tm-input"
            placeholder="XXXXXXX"
            maxlength="20"
            spellcheck="false"
            @input="onSuffixInput"
          />
        </div>
        <span class="tm-hint">Encontre o ID no painel do <a href="https://tagmanager.google.com" target="_blank" rel="noopener">Google Tag Manager</a> → Administrador → Container ID</span>
      </div>

      <!-- Preview -->
      <div v-if="form.gtm_id" class="tm-preview">
        <div class="tm-preview-header">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <polyline points="16,18 22,12 16,6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <polyline points="8,6 2,12 8,18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Código que será injetado
        </div>
        <pre class="tm-code">&lt;!-- Google Tag Manager --&gt;
&lt;script&gt;(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&amp;l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','{{ form.gtm_id }}');&lt;/script&gt;
&lt;!-- End Google Tag Manager --&gt;</pre>
      </div>

      <!-- Botão salvar -->
      <div class="tm-actions">
        <button
          class="tm-btn-save"
          :disabled="salvando || !form.gtm_id"
          @click="salvar"
        >
          <span v-if="salvando" class="tm-mini-spinner"></span>
          <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          {{ salvando ? 'Salvando...' : 'Salvar configuração' }}
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'TagManagerConfig',
  data() {
    return {
      loading: true,
      erro: null,
      salvando: false,
      form: { gtm_id: '', ativo: false },
      gtmSuffix: '',
    }
  },
  mounted() {
    this.buscarConfig()
  },
  methods: {
    async buscarConfig() {
      try {
        this.loading = true
        this.erro = null
        const { data } = await this.$http.get('tag-manager')
        this.form.gtm_id = data.gtm_id || ''
        this.form.ativo = !!data.ativo
        this.gtmSuffix = this.form.gtm_id.replace(/^GTM-/i, '')
      } catch (e) {
        this.erro = 'Falha ao carregar configuração do Tag Manager'
      } finally {
        this.loading = false
      }
    },

    onSuffixInput() {
      const clean = this.gtmSuffix.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
      this.gtmSuffix = clean
      this.form.gtm_id = clean ? `GTM-${clean}` : ''
    },

    async salvar() {
      if (!this.form.gtm_id) return
      this.salvando = true
      try {
        await this.$http.put('tag-manager', {
          gtm_id: this.form.gtm_id,
          ativo: this.form.ativo ? 1 : 0,
        })
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Tag Manager salvo com sucesso!',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (e) {
        const msg = e.response?.status === 400
          ? e.response.data?.error || 'ID inválido'
          : 'Falha ao salvar configuração'
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: msg, icon: 'XIcon', variant: 'danger' },
        })
      } finally {
        this.salvando = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
/* ── Paleta (tema escuro) ─────────────────── */
$orange: #E86A10;
$text: #e2e8f0;
$text-secondary: rgba(255,255,255,0.45);
$muted: rgba(255,255,255,0.35);
$border: rgba(255,255,255,0.09);
$card-bg: rgba(255,255,255,0.04);
$field-bg: rgba(255,255,255,0.06);

.tm-wrap {
  max-width: 720px;
  margin: 0 auto;
  padding: 28px 24px 48px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ── Header ───────────────────────────── */
.tm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.tm-header-left { display: flex; align-items: center; gap: 14px; }
.tm-header-icon {
  width: 40px; height: 40px; border-radius: 12px;
  background: rgba(232,106,16,0.1); border: 1px solid rgba(232,106,16,0.2);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.tm-header-title { font-size: 18px; font-weight: 800; color: $text; letter-spacing: -0.3px; }
.tm-header-sub { font-size: 12px; color: $muted; margin-top: 2px; }

/* ── Banner ───────────────────────────── */
.tm-banner {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 14px 16px; border-radius: 10px; margin-bottom: 20px;
  background: rgba(232,106,16,0.08); border: 1px solid rgba(232,106,16,0.2);
  font-size: 12px; line-height: 1.6; color: $text-secondary;
}
.tm-banner code {
  background: rgba(232,106,16,0.12); padding: 1px 6px; border-radius: 4px;
  font-size: 11px; color: $orange; font-weight: 600;
}
.tm-banner a { color: $orange; text-decoration: none; }

/* ── Loading / Error ─────────────────── */
.tm-loading {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; padding: 60px 0; color: $muted; font-size: 13px;
}
.tm-spinner {
  width: 20px; height: 20px; border-radius: 50%;
  border: 2px solid $border; border-top-color: $orange;
  animation: tmSpin 0.8s linear infinite;
}
@keyframes tmSpin { to { transform: rotate(360deg); } }

.tm-error {
  display: flex; align-items: center; gap: 10px;
  padding: 16px; border-radius: 10px;
  background: rgba(248,113,113,0.12); border: 1px solid rgba(248,113,113,0.25);
  color: #f87171; font-size: 13px;
}

/* ── Card ─────────────────────────────── */
.tm-card {
  background: $card-bg; border: 1px solid $border;
  border-radius: 16px; padding: 0; overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

/* ── Status bar ──────────────────────── */
.tm-status-bar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px;
  background: rgba(255,255,255,0.03);
  border-bottom: 1px solid $border;
  transition: background 0.3s;
}
.tm-status-bar--on { background: rgba(16,185,129,0.06); }
.tm-status-info { display: flex; align-items: center; gap: 12px; }
.tm-status-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
  background: #d1d5db; transition: background 0.3s;
}
.tm-status-dot--on { background: #10B981; box-shadow: 0 0 8px rgba(16,185,129,0.4); }
.tm-status-label { font-size: 14px; font-weight: 700; color: $text; }
.tm-status-desc { font-size: 11px; color: $muted; margin-top: 2px; }

/* ── Toggle ──────────────────────────── */
.tm-toggle { position: relative; display: inline-block; cursor: pointer; }
.tm-toggle input { display: none; }
.tm-toggle-track {
  display: block; width: 44px; height: 24px; border-radius: 12px;
  background: rgba(255,255,255,0.15); transition: background 0.2s;
}
.tm-toggle input:checked + .tm-toggle-track { background: $orange; }
.tm-toggle-thumb {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fff; transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.tm-toggle input:checked ~ .tm-toggle-track .tm-toggle-thumb { transform: translateX(20px); }

/* ── Field ───────────────────────────── */
.tm-field { padding: 24px; }
.tm-label {
  display: block; font-size: 11px; font-weight: 700; color: $text-secondary;
  text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px;
}
.tm-required { color: #dc2626; }
.tm-input-wrap {
  display: flex; align-items: center;
  background: $field-bg; border: 1.5px solid $border;
  border-radius: 10px; overflow: hidden; transition: border 0.2s;
}
.tm-input-wrap:focus-within { border-color: $orange; box-shadow: 0 0 0 3px rgba(232,106,16,0.1); }
.tm-input-prefix {
  padding: 0 0 0 16px; font-size: 14px; font-weight: 700;
  color: $orange; white-space: nowrap; user-select: none;
}
.tm-input {
  flex: 1; padding: 14px 16px 14px 4px; background: transparent;
  border: none; outline: none; color: $text; font-size: 14px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  letter-spacing: 1px; font-weight: 600;
}
.tm-input::placeholder { color: #b0b0c0; }
.tm-hint {
  display: block; font-size: 11px; color: $muted; margin-top: 8px; line-height: 1.5;
}
.tm-hint a { color: $orange; text-decoration: none; }

/* ── Preview ─────────────────────────── */
.tm-preview {
  margin: 0 24px 24px; border-radius: 10px; overflow: hidden;
  border: 1px solid $border;
}
.tm-preview-header {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(255,255,255,0.04);
  border-bottom: 1px solid $border;
  font-size: 11px; font-weight: 600; color: $muted;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.tm-code {
  margin: 0; padding: 14px 16px;
  background: #1e1e2e; color: #a0a0b8;
  font-size: 11px; line-height: 1.7; overflow-x: auto;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

/* ── Actions ─────────────────────────── */
.tm-actions {
  padding: 20px 24px; border-top: 1px solid $border;
  display: flex; justify-content: flex-end;
}
.tm-btn-save {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 28px; border-radius: 10px; border: none; cursor: pointer;
  background: linear-gradient(135deg, $orange, darken($orange, 5%));
  color: #fff; font-size: 13px; font-weight: 700; font-family: inherit;
  box-shadow: 0 4px 14px rgba(232,106,16,0.35);
  transition: opacity 0.2s, transform 0.15s;
}
.tm-btn-save:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.tm-btn-save:disabled { opacity: 0.4; cursor: not-allowed; }

.tm-mini-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  animation: tmSpin 0.6s linear infinite;
}
</style>
